import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Loader2, Lock, ArrowRight, Wrench, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { findVariant, productUrl, shipping, installTiers } from '../../content/products';
import { fmt, courierForCart, resolveTier, tierIncCents } from '../../lib/shopPricing';
import { isDigitalLock } from '../../lib/lockFit';
import { createCheckout, track, API_URL } from '../../lib/shopApi';
import { useCart } from '../../context/CartContext';
import InstallPicker from '../../components/shop/InstallPicker';
import BacksetPicker from '../../components/shop/BacksetPicker';
import { describeDoor } from '../../lib/doorAnswers';
import { Breadcrumbs } from '../../components/shop/ShopBits';
import usePageMeta from '../../hooks/usePageMeta';

const Field = ({ id, label, ...rest }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...rest} />
  </div>
);

// The size question belongs to a shipped line of a lock that comes in more than one size.
// It disappears in exactly two cases (Hugh, 22 Sep 2026): the lock was found through the
// decision tree (`fromChooser` — that customer never hears about the backset; the locksmith
// takes it off the door photos), or supply & install is ticked (the size is part of the
// booking process). A lock made in one size only has nothing to ask: the line already
// carries it, and the line says so.
function sizeMissing(line, product) {
  const sizes = product.fit.backsetsMm || [];
  if (sizes.length < 2 || line.fromChooser || line.install) return false;
  return typeof line.backsetMm !== 'number';
}

const CartPage = () => {
  usePageMeta();
  const { cart, quote, updateQty, updateLine, removeItem, setInstall, setShippingChoice, setPayment, setContact } = useCart();
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [nudged, setNudged] = useState({});
  const [areaNudge, setAreaNudge] = useState(false);
  const c = cart.contact || {};
  const shared = cart.install || {};

  useEffect(() => {
    if (cart.items.length) track('view_cart', { items: cart.items.length });
  }, [cart.items.length]);

  const lines = useMemo(() => cart.items.map((line) => ({ line, hit: findVariant(line.variantId) })).filter((x) => x.hit), [cart.items]);
  // A lock only sold fitted is always ticked (a stored cart from before the per-line tick may not say so).
  useEffect(() => {
    lines.forEach(({ line, hit }) => {
      if ((hit.product.saleStatus || 'sale') === 'install-only' && hit.product.install.eligible && !line.install) updateLine(line.lineId, { install: true });
    });
  }, [lines, updateLine]);
  // Supply & install is a tick on each line (Hugh, 22 Sep 2026); the visit's shared answers
  // (the service-area tick, notes, hub) appear once any line is ticked.
  const installed = lines.filter(({ line, hit }) => line.install && hit.product.install.eligible);
  const installOn = installed.length > 0;
  const installCount = installed.reduce((n, { line }) => n + (line.qty || 1), 0);
  const smart = installed.some(({ hit }) => hit.product.ecosystems && hit.product.ecosystems.length > 0);
  const extras = lines.length - installed.length;
  // An install order takes contact details only — the address comes with the booking (Hugh,
  // 22 Sep 2026); a shipped order needs somewhere to send the parcel.
  const contactOk = c.name && c.email && c.phone;
  const requiredOk = installOn ? contactOk : contactOk && c.address1 && c.city && c.postcode;
  // Door photos are asked for after payment (success page + confirmation email), never here
  // (Hugh, 17 Sep 2026): the customer pays first, the clarifying comes after.

  const checkout = async () => {
    setError('');
    if (!quote || quote.error) {
      setError((quote && quote.error) || 'Your cart is empty.');
      return;
    }
    const unsized = lines.filter(({ line, hit }) => sizeMissing(line, hit.product));
    if (unsized.length) {
      const next = {};
      unsized.forEach(({ line }) => { next[line.lineId] = true; });
      setNudged(next);
      const { hit } = unsized[0];
      setError(`Choose the ${hit.product.fit.sliding ? 'mortice size' : 'backset'} for the ${hit.product.shortName}${hit.product.install.eligible ? ' — or tick supply & install and we size it for you' : ''}.`);
      return;
    }
    if (installOn && !shared.inArea) {
      setAreaNudge(true);
      setError('Tick that the door is inside our Auckland service area — the list is one click away if you are not sure.');
      return;
    }
    if (!requiredOk) {
      setError(installOn ? 'Fill in your name, email and mobile so we can start the booking.' : 'Fill in your name, email, phone and delivery address.');
      return;
    }
    if (!agree) {
      setError('Please confirm you have read the shop terms, shipping & returns and warranty.');
      return;
    }
    setBusy(true);
    try {
      track('begin_checkout', { total: quote.totalCents / 100, payment: cart.payment, install: installOn, fitted: installCount });
      const res = await createCheckout({
        items: cart.items.map((l) => ({
          variantId: l.variantId,
          qty: l.qty,
          backsetMm: typeof l.backsetMm === 'number' ? l.backsetMm : null,
          noMortice: false,
          install: !!l.install,
          fromChooser: !!l.fromChooser,
          door: l.chooser ? describeDoor(l.chooser) || null : null,
        })),
        install: installOn ? {
          selected: true,
          inArea: true,
          hubSetup: !!shared.hubSetup,
          doorNotes: shared.doorNotes || null,
        } : null,
        shippingChoice: cart.shippingChoice,
        payment: cart.payment,
        contact: installOn ? { name: c.name, email: c.email, phone: c.phone, notes: '' } : c,
        expectedTotalCents: quote.totalCents,
      });
      if (res && res.url) {
        window.location.assign(res.url);
        return;
      }
      throw new Error('No payment page was returned.');
    } catch (err) {
      setError(err.message || 'Checkout is unavailable right now — please call us.');
      setBusy(false);
    }
  };

  if (!cart.items.length) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Your cart is empty</h1>
          <p className="text-gray-700 mb-6">Start with the lock that fits your door.</p>
          <Link to="/shop">
            <Button className="bg-red-600 hover:bg-red-700 text-white font-bold">
              Back to the shop <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: 'Cart' }]} />
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">Your cart</h1>
        <div className="grid lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2 space-y-8">
            {/* Items — each line carries its own backset and its own supply & install tick */}
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-xl" data-testid="cart-items">
              {lines.map(({ line, hit }) => {
                const { product, variant } = hit;
                const eligible = !!product.install.eligible;
                const installLocked = (product.saleStatus || 'sale') === 'install-only';
                const fitted = eligible && (!!line.install || installLocked);
                const tier = resolveTier(product);
                const unit = tierIncCents(tier);
                const backsets = product.fit.backsetsMm || [];
                const sized = backsets.length > 1;
                // A one-size lock is settled at add-to-cart; say the size so the line does not
                // look as though something is missing (Hugh, 22 Sep 2026 — the 805W is 60 mm only).
                const fixedSize = !sized && backsets.length === 1 && !line.fromChooser ? backsets[0] : null;
                return (
                  <li key={line.lineId} className="p-4" data-testid={`cart-line-${product.slug}`}>
                    <div className="flex gap-4 items-start">
                      <img src={product.images[0].sm || product.images[0].src} alt="" width={80} height={80} className="w-20 h-20 object-contain bg-white border border-gray-100 rounded-md flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <Link to={productUrl(product)} className="font-semibold text-gray-900 hover:text-blue-700">
                          {product.name}
                        </Link>
                        <p className="text-sm text-gray-600">
                          {variant.label}
                          {product.mortice ? ' · mortice included' : ''}
                          {fixedSize ? ` · ${product.fit.sliding ? 'mortice' : 'backset'} ${fixedSize} mm (the only size it comes in)` : ''}
                        </p>
                        <div className="mt-2 flex items-center gap-3">
                          <label className="text-sm text-gray-700">
                            Qty{' '}
                            <select value={line.qty} onChange={(e) => updateQty(line.lineId, Number(e.target.value))} className="ml-1 rounded-md border border-gray-300 px-2 py-1 bg-white">
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                            </select>
                          </label>
                          <button type="button" onClick={() => removeItem(line.lineId)} className="text-sm text-gray-500 hover:text-red-600 inline-flex items-center gap-1" aria-label={`Remove ${product.shortName}`}>
                            <Trash2 size={14} /> Remove
                          </button>
                        </div>
                      </div>
                      <p className="font-semibold text-gray-900 whitespace-nowrap">{fmt(Math.round(product.priceIncGst * 100) * line.qty)}</p>
                    </div>

                    {/* The backset, asked here once the lock is in the cart — and only here.
                        It goes when the lock came from the decision tree or when supply &
                        install is ticked; a one-size lock never shows it (see `fixedSize`). */}
                    {sized && !line.fromChooser && !fitted && (
                      <BacksetPicker
                        product={product}
                        value={line.backsetMm}
                        onChange={(mm) => { updateLine(line.lineId, { backsetMm: mm }); setNudged((n) => ({ ...n, [line.lineId]: false })); setError(''); }}
                        nudge={!!nudged[line.lineId]}
                      />
                    )}

                    {eligible && (
                      <label className="mt-3 flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-3 hover:border-gray-400" data-testid={`line-install-${product.slug}`}>
                        <input
                          type="checkbox"
                          checked={fitted}
                          disabled={installLocked}
                          onChange={(e) => updateLine(line.lineId, { install: e.target.checked, backsetMm: e.target.checked && sized ? null : line.backsetMm })}
                          className="mt-1 h-4 w-4"
                          data-testid="line-install-toggle"
                        />
                        <span className="min-w-0">
                          <span className="font-semibold text-gray-900 inline-flex flex-wrap items-center gap-x-2">
                            <Wrench size={16} className="text-red-600" /> Supply &amp; install in Auckland
                            <span className="text-red-700">{fmt(unit)} {isDigitalLock(product) ? 'per door' : 'each'}{line.qty > 1 ? ` × ${line.qty}` : ''}</span>
                          </span>
                          <span className="block text-sm text-gray-600">{installLocked ? 'Only sold fitted.' : installTiers[tier].label}</span>
                          {fitted && line.chooser && describeDoor(line.chooser) && (
                            <span className="block text-sm text-gray-800 mt-1" data-testid="line-door-known">
                              <CheckCircle size={14} className="inline text-green-600 mr-1 -mt-0.5" />
                              {describeDoor(line.chooser)}
                            </span>
                          )}
                        </span>
                      </label>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Install — what the visit shares, once any line is ticked */}
            {installOn && (
              <section className="rounded-xl border border-gray-200 p-5" data-testid="cart-install">
                <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2">
                  <Wrench size={16} className="text-red-600" /> Installation — {installCount} {installCount === 1 ? 'door' : 'doors'}
                </h2>
                {/* One line (Hugh, 22 Sep 2026: every word fights for its place). Nothing about photos before payment. */}
                <p className="text-sm text-gray-700 mt-1 mb-4" data-testid="cart-install-explainer">
                  <span className="font-semibold text-gray-900">We take it from here.</span> A fixed price to fit {installCount === 1 ? 'this door' : 'each door'}; the lock arrives with our installer. After you pay we get in touch straight away to start the booking process.
                </p>
                <InstallPicker value={shared} onChange={(v) => { setInstall(v); if (v.inArea) { setAreaNudge(false); setError(''); } }} quote={quote} smart={smart} extras={extras} nudge={areaNudge} />
              </section>
            )}

            {/* Shipping */}
            {!installOn && (
              <section className="rounded-xl border border-gray-200 p-5">
                <h2 className="font-semibold text-gray-900 mb-3">Delivery</h2>
                <p className="text-sm text-gray-600 mb-3">
                  {shipping.courierLine} · charged per parcel, added to every shipped order.
                  {courierForCart(cart.items, 'standard').parcels > 1 ? ` This order ships as ${courierForCart(cart.items, 'standard').parcels} parcels from ${courierForCart(cart.items, 'standard').parcels} suppliers.` : ''}
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    ['standard', `Courier ${fmt(courierForCart(cart.items, 'standard').cents)}`],
                    ['rural', `Rural delivery ${fmt(courierForCart(cart.items, 'rural').cents)}`],
                  ].map(([k, label]) => (
                    <label key={k} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${cart.shippingChoice === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-300 text-gray-700'}`}>
                      <input type="radio" name="shipping" value={k} checked={cart.shippingChoice === k} onChange={() => setShippingChoice(k)} className="sr-only" />
                      {label}
                    </label>
                  ))}
                </div>
              </section>
            )}

            {/* Contact + address */}
            <section className="rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-1">{installOn ? 'Your details' : 'Your details and delivery address'}</h2>
              {installOn && <p className="text-sm text-gray-600 mb-3">To book you in. The address comes with the booking.</p>}
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="c-name" label="Name" value={c.name || ''} onChange={(e) => setContact({ name: e.target.value })} required autoComplete="name" />
                <Field id="c-email" label="Email" type="email" value={c.email || ''} onChange={(e) => setContact({ email: e.target.value })} required autoComplete="email" />
                <Field id="c-phone" label="Mobile" value={c.phone || ''} onChange={(e) => setContact({ phone: e.target.value })} required autoComplete="tel" />
                {!installOn && (
                  <>
                    <Field id="c-address1" label="Street address" value={c.address1 || ''} onChange={(e) => setContact({ address1: e.target.value })} required autoComplete="address-line1" />
                    <Field id="c-address2" label="Unit / building (optional)" value={c.address2 || ''} onChange={(e) => setContact({ address2: e.target.value })} autoComplete="address-line2" />
                    <Field id="c-suburb" label="Suburb" value={c.suburb || ''} onChange={(e) => setContact({ suburb: e.target.value })} autoComplete="address-level3" />
                    <Field id="c-city" label="City / town" value={c.city || ''} onChange={(e) => setContact({ city: e.target.value })} required autoComplete="address-level2" />
                    <Field id="c-postcode" label="Postcode" value={c.postcode || ''} onChange={(e) => setContact({ postcode: e.target.value })} required autoComplete="postal-code" inputMode="numeric" />
                  </>
                )}
              </div>
              {/* An install order has its note box in the Installation section; no browser
                  autofill on this one (Hugh, 22 Sep 2026: a typed note kept coming back). */}
              {!installOn && (
              <div className="mt-4">
                <Label htmlFor="c-notes">Notes for us (optional)</Label>
                <Input id="c-notes" value={c.notes || ''} onChange={(e) => setContact({ notes: e.target.value })} autoComplete="off" />
              </div>
              )}
            </section>

            {/* Payment */}
            <section className="rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-3">How would you like to pay?</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ['card', 'Card, Apple Pay, Google Pay or Afterpay', 'Secure payment page by Stripe.'],
                  ['poli', 'Pay from your bank (POLi)', 'Log in to your NZ internet banking — no card needed.'],
                ].map(([k, label, sub]) => (
                  <label key={k} className={`rounded-lg border p-4 cursor-pointer ${cart.payment === k ? 'border-gray-900 bg-gray-50' : 'border-gray-300'}`}>
                    <input type="radio" name="payment" value={k} checked={cart.payment === k} onChange={() => setPayment(k)} className="sr-only" />
                    <span className="font-semibold text-gray-900 block">{label}</span>
                    <span className="text-sm text-gray-600">{sub}</span>
                  </label>
                ))}
              </div>
            </section>
          </div>

          {/* Summary */}
          <aside className="rounded-2xl border border-gray-200 p-6 lg:sticky lg:top-40" data-testid="order-summary">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Order summary</h2>
            {quote && !quote.error ? (
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-600">Items</dt>
                  <dd className="text-gray-900">{fmt(quote.itemsCents)}</dd>
                </div>
                {quote.install && quote.install.tiers.map((t) => (
                  <div key={t.tier} className="flex justify-between" data-testid={`summary-install-${t.tier}`}>
                    <dt className="text-gray-600">
                      Installation — {t.tierLabel}
                      {t.count > 1 ? ` × ${t.count}` : ''}
                    </dt>
                    <dd className="text-gray-900">{fmt(t.cents)}</dd>
                  </div>
                ))}
                {quote.install && quote.install.hubSetupCents > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Smart hub &amp; app setup</dt>
                    <dd className="text-gray-900">{fmt(quote.install.hubSetupCents)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-600">{quote.shippingLabel}</dt>
                  <dd className="text-gray-900">{quote.shippingCents ? fmt(quote.shippingCents) : '—'}</dd>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-3 text-base">
                  <dt className="font-bold text-gray-900">Total incl. GST</dt>
                  <dd className="font-bold text-gray-900" data-testid="cart-total">
                    {fmt(quote.totalCents)}
                  </dd>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <dt>Includes GST of</dt>
                  <dd>{fmt(quote.gstCents)}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-amber-700">{quote ? quote.error : 'Add an item to see the total.'}</p>
            )}

            <label className="flex items-start gap-2 mt-5 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="agree-terms" />
              <span>
                I have read the{' '}
                <Link to="/shop/terms" className="underline">
                  shop terms
                </Link>
                ,{' '}
                <Link to="/shop/shipping-returns" className="underline">
                  shipping &amp; returns
                </Link>{' '}
                and{' '}
                <Link to="/shop/warranty" className="underline">
                  warranty
                </Link>
                .
              </span>
            </label>

            {error && (
              <p className="mt-3 text-sm text-red-700" role="alert" data-testid="checkout-error">
                {error}
              </p>
            )}
            <Button onClick={checkout} disabled={busy || !quote || !!quote.error} className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white font-bold py-3" data-testid="checkout-button">
              {busy ? <Loader2 className="mr-2 animate-spin" size={18} /> : <Lock className="mr-2" size={18} />}
              {cart.payment === 'poli' ? 'Pay with POLi' : 'Pay securely'}
            </Button>
            {!API_URL && <p className="mt-2 text-xs text-amber-700">Checkout is not connected in this build (REACT_APP_BACKEND_URL unset).</p>}
            <p className="mt-3 text-xs text-gray-500">Prices include GST. A GST receipt is emailed with your confirmation.</p>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
